/**
 * debtExit — что делать человеку с долгом по кредитам (WEB-2845 п.3, макет
 * WEB-2823 `design/billing-mockup/`, демо «Долг: есть»).
 *
 * Решение владельца 08.10: выход из долга зависит от ОДНОГО признака —
 * включена ли продажа пакетов пополнения (`TOPUP_ENABLED`, WEB-248):
 *  · включена  → «Пополните баланс на N или больше — долг погасится сразу»
 *                и кнопка «Пополнить» на страницу баланса организации;
 *  · выключена → «Напишите в поддержку — разберём операцию и снимем
 *                ограничение», без кнопки (решение владельца 09.10: канала
 *                поддержки в кабинете нет, вести кнопку некуда).
 * Участник без прав на деньги при любом состоянии продажи читает «Погасить
 * долг может владелец организации.» без кнопки (решение 09.10).
 * Фразы «со следующим начислением» нет: по ADR-0024 грантов нет.
 *
 * Одна развилка на все места — плашку редактора (EditorTopUpButton), строку
 * кошелька (WalletBalanceCard) и строку долга блока «Требует внимания»
 * (`components/attention/attentionView.ts`, WEB-3781: право — `action.allowed`
 * сервера, сумма — только из ответа), чтобы человек везде читал одно и то же
 * (так и в макете: `debtExit`). Плашка каркаса PastDueBanner снята WEB-3782 —
 * долг на любом экране теперь строка блока и число у «Главной» в меню.
 *
 * Деньги здесь не считаются: сумма долга (`debtCents`, 1 Cr = 1 цент) и
 * признак блокировки приходят с сервера готовыми, модуль только выбирает
 * фразу.
 */
import { TOPUP_ENABLED } from '@/config/topup-enabled';
import { useOrgRole } from '@/hooks/useOrgRole';
import { formatNumber, pickStrings } from '@/i18n';
import { billingStrings } from '@/i18n/dictionaries/billing';

export type DebtExitKind = 'topup' | 'support' | 'owner';

/** Чистая развилка: кто смотрит и продаются ли пакеты. */
export function resolveDebtExit(input: {
  canSettle: boolean;
  topupEnabled: boolean;
}): DebtExitKind {
  if (!input.canSettle) return 'owner';
  return input.topupEnabled ? 'topup' : 'support';
}

/** Сумма долга так же, как кошелёк печатает кредиты: «12 500 Cr». */
export function formatDebtCredits(debtCents: number): string {
  return `${formatNumber(debtCents)} Cr`;
}

/** Фраза выхода из долга — плашка редактора и строка долга блока «Требует внимания». */
export function debtExitLine(kind: DebtExitKind, debtCents: number): string {
  const s = pickStrings(billingStrings);
  if (kind === 'owner') return s.debtExitOwner;
  if (kind === 'support') return s.debtExitSupport;
  // Суммы нет (обзор ещё не пришёл) — без выдуманного «0 Cr».
  return debtCents > 0
    ? s.debtExitTopUp(formatDebtCredits(debtCents))
    : s.debtExitTopUpNoAmount;
}

/** Короткая фраза после «Долг N Cr —» в карточке кошелька. */
export function debtExitWalletNote(kind: DebtExitKind): string {
  const s = pickStrings(billingStrings);
  if (kind === 'owner') return s.walletDebtOwner;
  if (kind === 'support') return s.walletDebtSupport;
  return s.walletDebtTopUp;
}

/**
 * Развилка для текущего человека. Право погасить — тот же ролевой гейт, что
 * был у кнопки «Пополнить» (ревизия десятки 8): владелец или администратор
 * организации; роль неизвестна (список организаций не загружен или упал) —
 * считаем, что может: настоящего владельца прятать нельзя, страница баланса
 * сама объяснит отказ. Признак продажи читается в момент вызова.
 *
 * Звать только там, где в сторе есть срез организаций (плашки и строка
 * долга монтируются только при долге).
 */
export function useDebtExit(): DebtExitKind {
  return debtExitFor(useOrgRole());
}

/**
 * Та же развилка по уже прочитанной роли (активная организация — плашка
 * каркаса и кошелёк, через useDebtExit).
 */
export function debtExitFor(org: {
  isOrgAdmin: boolean;
  role: string | null;
}): DebtExitKind {
  return debtExitForRight(org.isOrgAdmin || org.role === null);
}

/**
 * Развилка по уже решённому праву погасить. Плашка редактора
 * (banners/EditorTopUpButton.tsx) берёт право из двери useOrgTopUp
 * (WEB-1895: судит организация ОТКРЫТОГО проекта, а не активная; роль
 * неизвестна — право есть) и только выбирает здесь фразу по продаже пакетов.
 * Признак продажи читается в момент вызова.
 */
export function debtExitForRight(canSettle: boolean): DebtExitKind {
  return resolveDebtExit({ canSettle, topupEnabled: TOPUP_ENABLED });
}
